import { SUPPORT_CATEGORIES, SUPPORT_LIMITS, SUPPORT_MSG, SUPPORT_TOKEN_RE, type SupportReplyRequest, type SupportThread } from "@genbaryoku/contract";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useState } from "react";
import { Link, useParams } from "react-router";
import { api, ApiError, messageOf } from "../api.js";
import { usePageName } from "../components/BackButton.jsx";
import { useImageViewer } from "../components/ImageViewer.jsx";
import { Honeypot, useImagePicks } from "../components/Support.jsx";
import { AuthCard, ErrorText, Field } from "../components/ui.jsx";

import { ymdHmShort } from "../lib/dates.js";
/**
 * 返事のリンクのページ（/support/t/:token、#124 の 3a、docs/50 §2.1）。メールの「返信する」「内容を書き足す」のボタンから。
 *   ログイン不要（リンクの署名で本人とみなす。7日で切れる）。やりとりの一覧（運営の社内メモは入らない）と返事の欄。
 *   中身は製品の API が管理サーバーの受け口から取ってくる（止まっていれば「いまは表示できません」）。
 *   書いた返事は送り箱に入って届く＝すぐには一覧に出ないので、送ったら知らせの文を出し、少しあとに読み直す
 */
export function SupportThreadPage() {
  const { token = "" } = useParams();
  usePageName(SUPPORT_MSG.replyTitle, SUPPORT_MSG.title); // 戻るボタンは「を」「の」なし（#240）
  const ok = SUPPORT_TOKEN_RE.test(token);
  const q = useQuery({
    queryKey: ["support-thread", token],
    queryFn: () => api<SupportThread>("GET", `/support/t/${encodeURIComponent(token)}`),
    enabled: ok,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const code = q.error instanceof ApiError ? q.error.code : undefined;
  const failText = !ok || code === "SUPPORT_LINK_BAD" ? SUPPORT_MSG.bad : code === "SUPPORT_LINK_EXPIRED" ? SUPPORT_MSG.expired : q.isError ? messageOf(q.error) : undefined;
  return (
    <AuthCard title={SUPPORT_MSG.replyTitle} plain>
      {failText ? (
        <div className="supfail" data-testid="support-thread-error">
          <ErrorText>{failText}</ErrorText>
          {code !== "SUPPORT_UNAVAILABLE" ? (
            <Link className="btn" to="/contact">
              {SUPPORT_MSG.title}
            </Link>
          ) : null}
        </div>
      ) : !q.data ? (
        <p className="hint">読み込んでいます…</p>
      ) : (
        <Thread token={token} t={q.data} />
      )}
    </AuthCard>
  );
}

/** 「26/11/05 01:58」（#320 ★8＝画面の年月日は年2桁） */
const fmt = (iso: string) => ymdHmShort(iso);

/**
 * やりとりの画像。id のあるもの（ログインしてから送った問い合わせ）は見本を出し、押すと大きく見る窓（ImageViewer）。
 * id の無いもの（ログイン前の問い合わせ）は名前だけ（宛先を確かめていないリンクで画像を見せない）
 */
function ThreadFiles({ token, files }: { token: string; files: SupportThread["messages"][number]["files"] }) {
  const viewer = useImageViewer();
  if (!files.length) return null;
  const shown = files.filter((f) => f.id).map((f) => ({ id: f.id!, name: f.name, src: `/api/v1/support/t/${encodeURIComponent(token)}/files/${f.id}` }));
  const names = files.filter((f) => !f.id);
  return (
    <>
      {shown.length ? (
        <ul className="supthfiles" data-testid="support-thread-files">
          {shown.map((f, i) => (
            <li key={f.id}>
              <button type="button" className="supthumb" onClick={() => viewer.open(shown, i)} aria-label={`${f.name} を大きく表示`}>
                <img src={f.src} alt={f.name} loading="lazy" />
              </button>
              <span>{f.name}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {names.length ? <p className="hint">画像 {names.map((f) => f.name).join("・")}</p> : null}
      {viewer.node}
    </>
  );
}

function Thread({ token, t }: { token: string; t: SupportThread }) {
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  const [website, setWebsite] = useState("");
  const [err, setErr] = useState<string | undefined>();
  const [sent, setSent] = useState(false);
  const bodyId = useId();
  const send = useMutation({
    mutationFn: (b: SupportReplyRequest) => api<{ ok: true }>("POST", `/support/t/${encodeURIComponent(token)}/reply`, b),
    onSuccess: () => {
      pics.clear();
      setBody("");
      setSent(true);
    },
    onError: (e) => setErr(messageOf(e)),
  });
  const pics = useImagePicks(setErr, send.isPending);
  // 送り箱から届くまで少しかかる。送ったあと 3秒で読み直す
  useEffect(() => {
    if (!sent) return;
    const h = setTimeout(() => void qc.invalidateQueries({ queryKey: ["support-thread", token] }), 3000);
    return () => clearTimeout(h);
  }, [sent, qc, token]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (send.isPending || pics.reading) return;
    if (!body.trim()) return setErr("内容を書いてください");
    setErr(undefined);
    setSent(false);
    send.mutate({ body: body.trim(), ...pics.attachments, ...(website ? { website } : {}) });
  };
  const busy = send.isPending || pics.reading;
  return (
    <>
      <p className="supno" data-testid="support-thread-no">
        受付番号 <b>{t.no}</b>
        <span>{SUPPORT_CATEGORIES[t.category]}</span>
      </p>
      {t.status === "closed" ? <p className="hint">{SUPPORT_MSG.closed}</p> : null}
      <ol className="supthread" data-testid="support-thread">
        {t.messages.map((m, i) => (
          <li key={i} className={m.from}>
            <div className="suphead">
              <b>{m.name}</b>
              <time dateTime={m.at}>{fmt(m.at)}</time>
            </div>
            <p className="supmsg">{m.body}</p>
            <ThreadFiles token={token} files={m.files} />
          </li>
        ))}
      </ol>
      <form className="supform" onSubmit={submit} noValidate data-testid="support-reply-form">
        <Field id={bodyId} label={<>{SUPPORT_MSG.body}<span className="req">必須</span></>}>
          <textarea id={bodyId} className="inp supbody" rows={5} maxLength={SUPPORT_LIMITS.bodyMax} value={body} onChange={(e) => setBody(e.target.value)} data-testid="support-reply-body" />
        </Field>
        {pics.node}
        <Honeypot value={website} onChange={setWebsite} />
        <ErrorText>{err}</ErrorText>
        {sent ? (
          <p role="status" className="supsent" data-testid="support-replied">
            {SUPPORT_MSG.replied}
          </p>
        ) : null}
        <button type="submit" className="btn primary big" disabled={busy} data-testid="support-reply-send">
          {SUPPORT_MSG.reply}
        </button>
      </form>
    </>
  );
}
